import {_, classNames, React, observer, lockr, useAsObservableSource, useEffect, useLayoutEffect} from '@api'
import {imageBreakpoints} from '@stores'
import {LazyImage} from 'centric-ui-components'
import {ImageUrlWithHD, useVipLocalStore, VipLocalStore} from '@modules'
import {aws, nav, StoresInstance, ui} from '@stores'
import {action, autorun, computed, observable, reaction, runInAction} from 'mobx'
import {ReactEventHandler} from 'react'
import {useMeasure} from 'react-use'
import * as css from './MyImage.css'
import {LinkOff} from '@material-ui/icons'

const MaxRetryCount = 1

interface MyProps extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
  imageUrl?: ImageUrlWithHD | string
  lazy?: boolean
  linkUrl?: string
  width?: number,
  height?: number
  showCode?: boolean
  onQrImageLoad?: ReactEventHandler<HTMLImageElement>
  onGetBackgroundColor?: (rgb: [r: number, g: number, b: number]) => void
  draggable?: boolean
  loader?: React.ReactNode
  customLinkOff?: boolean
  largeImage?: boolean
  blurOnLoad?: boolean
  filename?: string
  objectFit?: 'cover' | 'contain'
  noImageContent?: React.ReactNode
  convertToDataURL?: (dataURL: string, renderWidth: number, renderHeight: number) => void
  'data-active'?: boolean
  'data-index'?: number
  'data-path-index'?: number
  'data-path'?: string
  onContextMenu?: React.MouseEventHandler
  onMeasureImage?: (e: { width: number, height: number, image: HTMLImageElement }) => void
  onCreateStore?: (store: MyImageStore) => void
  addAuthHeader?: boolean
  c8ImageUrl?: string

  transparent?: boolean
  imageId?: string
  rawImageSize?: number
}

/// region Store

export class MyImageStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Partial<MyProps>) {
    super(stores)

    this.retryFromC8 = stores.deployments.deploymentConfig.settings.images?.alwaysUseC8 === true
  }

  @computed get loaded() {
    const {noImage, image, imageLoadError} = this
    return (image != null && !imageLoadError) || noImage
  }

  @computed get hasError() {
    const {imageLoadError} = this
    return imageLoadError != null && imageLoadError !== false
  }

  /**
   * For drag and drop, generated via rendering internally to a canvas and capturing it
   */
  @observable dataUrl: string = null

  @computed get imageUrl() { return (this.props.imageUrl ?? this.props.src)}

  registerEffects = () => {
    const {props, props: {}, dragDrop} = this

    const toDispose = [
      autorun(() => {
        const {props: {onCreateStore}} = this
        onCreateStore && onCreateStore(this)
      }),
      reaction(() => ({isDragging: dragDrop.isDragging, convert: props.convertToDataURL}), () => {
        if (!this.dataUrl && this.image && props.convertToDataURL && !dragDrop.isDragging) {
          this.loadDataURL()
        }
      }),
      autorun(
        () => {
          if (!this.props.showCode) return

          const {createQrCode, image: rawImage, defaultImageUrl, props: {showCode}, imageUrl} = this

          if ((rawImage || !imageUrl || defaultImageUrl.startsWith('#')) && showCode) {
            try {
              if (rawImage && this.loadingImageSrc == rawImage.src) {
                const image = new Image()
                image.crossOrigin = 'Anonymous'
                this.loadingImageSrc = rawImage.src
                image.src = rawImage.src
                if (image.src.indexOf('?') == 1) {
                  image.src += '?'
                } else {
                  image.src += '&'
                }
                image.src += `random=${Math.random()}`

                image.onload = () => {
                  this.loadingImageSrc = null
                  createQrCode(image)
                }
              } else {
                setTimeout(() => createQrCode(rawImage))
              }
            } catch (err) {
              console.error(err)
            }
          }
        },
        {
          name: `Potentially create QR code image`
        })
    ]

    return () => toDispose.forEach(d => d())
  }

  @observable animateOnLoad = false

  @computed get highDefUrl() {
    const {imageUrl, addSecurityToken, doServerlessImport, retryFromC8, stores: {authStore: {securityTokenURL}}, c8ImageUrl} = this,
      r = _.isString(imageUrl) ? imageUrl : imageUrl?.highDefUrl

    return doServerlessImport && !retryFromC8 ? this.serverlessImportUrl : retryFromC8 && c8ImageUrl ? c8ImageUrl : addSecurityToken ? `${r}?SecurityTokenUrl=${securityTokenURL}` : r
  }

  @computed get serverlessImportUrl() {
    if (this.retryFromC8) return undefined

    const {stores: {authStore: {securityTokenURL}, deployments: {hostName, deploymentEnv}}, props: {transparent, imageId, rawImageSize}} = this,
      {importImageApiGatewayUrl, cloudConfig} = aws
    // Todo - env should be removed and looked up via SAAS.  API gateway URL should be stored in aws config table
    // return `https://r1smiyfjyl.execute-api.us-west-2.amazonaws.com/Prod/import/c8-image?host=${hostName ?? location?.hostname?.split('.')?.[0]}&env=${deploymentEnv}&imageId=${imageId}&size=${!rawImageSize ? 'full' : `!${rawImageSize},${rawImageSize}`}&tr=${transparent ? 1 : 0}&SecurityTokenUrl=${securityTokenURL}`
    return !importImageApiGatewayUrl || !imageId ? undefined : `${importImageApiGatewayUrl}/Prod/push-image?host=${hostName ?? cloudConfig.bucket ?? location?.hostname?.split('.')?.[0]}&env=${cloudConfig.env ?? deploymentEnv}&imageId=${imageId}&SecurityTokenUrl=${securityTokenURL}`
  }

  @computed get c8ImageUrl() {
    const {catalog, props: {transparent, imageId, rawImageSize}} = this
    if (!imageId) return null

    return catalog.imageUrlFor(imageId, {autoTransparency: transparent, imageSize: rawImageSize ? {width: rawImageSize, height: rawImageSize} : null, c8Size: 'largeImage', source: 'c8'})
  }

  @computed get defaultImageUrl() {
    const {imageUrl, serverlessImportUrl, doServerlessImport, retryFromC8, addSecurityToken, stores: {authStore: {securityTokenURL}}, c8ImageUrl} = this,
      r = _.isString(imageUrl) ? imageUrl : (imageUrl?.defaultImageUrl ?? imageUrl?.highDefUrl)

    return retryFromC8 && c8ImageUrl ? c8ImageUrl : doServerlessImport && serverlessImportUrl ? serverlessImportUrl : addSecurityToken ? `${r}?SecurityTokenUrl=${securityTokenURL}` : r
  }

  @computed get srcSet() {
    const {imageUrl} = this

    if (_.isString(imageUrl) && imageUrl.indexOf('/full') != -1) {
      return imageBreakpoints.map(n => `${imageUrl.replace('/full', `!${n},${n}`)} ${n}px`).join(',')
    }

    return undefined
  }

  @computed get imageSrc() {
    const {highDefUrl, retryCount, defaultImageUrl, hasTriedServerlessImport, doServerlessImport, retryFromC8, addSecurityToken} = this
    let r = highDefUrl ? (highDefUrl.indexOf('/') != -1 ? highDefUrl : `data:image/png;base64, ${highDefUrl}`)
                       : defaultImageUrl ? defaultImageUrl.indexOf('/') != -1 ? defaultImageUrl : `data:image/png;base64, ${defaultImageUrl}` : null,
      hasAddedQueryParam = false

    if (!retryFromC8 && !addSecurityToken) {
      if (hasTriedServerlessImport && !doServerlessImport) {
        r += `?timestamp=${Date.now()}`
        hasAddedQueryParam = true
      }

      if (retryCount > 0) {
        r += `${hasAddedQueryParam ? '&' : '?'}retryCount=${retryCount}`
      }
    }
    return r
  }

  @computed get noImage() {
    const {imageLoadError, defaultImageUrl, imageUrl} = this
    return _.isEmpty(imageUrl) || !defaultImageUrl || imageLoadError || defaultImageUrl.startsWith('#') || defaultImageUrl.startsWith('rgba(')
  }

  @computed get imageStyle() {
    const {props: {style}, imageSrc} = this
    return (
      // return _.merge<CSSProperties, CSSProperties>(ui.settings.cantaloupeImageServerUrl && imageSrc ? {
      //     '--image-url': `url('${imageSrc}')`,
      //     '--bitonal-image-url': `url('${imageSrc.replace('/default.', '/bitonal.')}')`
      // } as CSSProperties : {},
      style
    )
  }

  @action setImageRef = (img: HTMLImageElement) => {
    // Handle browser cached image loading
    if (img?.complete) {
      this.image = img
      this._onImageLoad()
    } else {
      this.animateOnLoad = true
    }
  }

  @action loadDataURL = () => {
    const {props: {convertToDataURL}, image, dragDrop} = this
    if (!dragDrop.isDragging && convertToDataURL && !this.dataUrl) {
      // console.log(`Create data URL`)
      const canvas = document.createElement('canvas'),
        context = canvas.getContext('2d')

      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      image.setAttribute('crossorigin', 'anonymous')

      context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight)
      this.dataUrl = canvas.toDataURL('image/png')

      convertToDataURL(this.dataUrl, image.naturalWidth, image.naturalHeight)//this.image.width, this.image.height)
      canvas.remove()
    }
  }

  _onImageLoad = async () => {

    // if (onGetBackgroundColor) {
    //   const v = new Vibrant(image)
    //   console.log(v)
    //   const pal = await v.getPalette()
    //   console.log(pal)
    // }

    // this.loadDataURL()  Super expensive!  We have to draw every image twice!  Do this a different way.
  }

  @action onImageLoad = async (e: React.UIEvent<HTMLImageElement>) => {
    // console.log(`Image Load: ${this.props.imageId}`)

    this.imageLoadError = false
    if (e) {
      this.image = e.currentTarget
      this._onImageLoad()
    }
  }

  @observable imageLoadError = false

  /**
   * We try to get the image from cloudfront without any token (as it changes the URL and invalidates caching)
   * If that fails, we attach a query parameter with our token and try again
   */
  @observable addSecurityToken = false
  @observable retryFromC8 = false
  @observable doServerlessImport = false
  @observable hasTriedServerlessImport = false

  @observable retryCount = 0
  @action onImageLoadError = (e: React.UIEvent<HTMLImageElement>) => {
    // console.log(`Image Load Error: ${this.props.imageId}`)
    const {retryFromC8} = this
    if (aws.useCloudFront) {
      if (this.retryCount < MaxRetryCount && !this.doServerlessImport && !this.hasTriedServerlessImport) { // It takes some time for serverless/lambda to process new images
        setTimeout(() => this.retryCount++, 1000)
        return
      } else if (!retryFromC8 && DEBUG && !this.doServerlessImport && !this.hasTriedServerlessImport && this.props.imageId && this.serverlessImportUrl != null) {
        // console.log(`Image load error: `, this.defaultImageUrl)
        this.hasTriedServerlessImport = true
        this.retryCount = 0
        this.doServerlessImport = true
        return
      } else if (this.doServerlessImport) {
        this.retryCount = 0
        this.doServerlessImport = false
        this.imageLoadError = false
      } else if (!this.retryFromC8) {
        this.retryFromC8 = true
        this.addSecurityToken = true
      } else {
        this.imageLoadError = true
        return
      }
    } else {
      // console.debug(`onImageLoadError`, this.imageSrc)
      if (this.addSecurityToken && !this.retryFromC8) {
        this.retryFromC8 = true
        this.addSecurityToken = false
      } else if (!this.addSecurityToken && !this.retryFromC8) {
        this.addSecurityToken = true
      } else {
        this.imageLoadError = true
        // if (e.currentTarget.src.indexOf('TokenURL=centric') != -1) {
        //   console.warn(`Error loading C8 image - possible token expiration`)
        //   if (!lockr.get('SecurityTokenURL') && !DEBUG) {
        //     nav.routeToLogoutPage()
        //   }
        // }
      }
    }
  }

  @observable.ref image: HTMLImageElement = null
  @observable.ref qrImage = null

  @action createQrCode = async (image?: HTMLImageElement) => {
    if (ui.settings.qr) {
      const {defaultImageUrl, props: {width, linkUrl}} = this
      // const {AwesomeQR} = await import(/* webpackChunkName: "qr" */ './AwesomeQR')
      // AwesomeQR.create({
      //   size: width ?? 100,
      //   //logoImage: image,
      //   dotScale: .5,
      //   backgroundImage: image ? image : undefined,
      //   //backgroundDimming: 'rgba(127,127,127,.5)',
      //   ...utils.colorsForImage(defaultImageUrl),
      //
      //   margin: 0,
      //   //gifBackground: '#efefef',
      //   maskedDots: false,
      //   text: linkUrl,
      //   callback: (dataURI) => {
      //     this.qrImage = dataURI
      //   }
      // })
    }
  }

  loadingImageSrc: string
}

export const MyImageStoreContext = React.createContext<MyImageStore>(null)
export const useMyImageStore = () => React.useContext(MyImageStoreContext)

/// endregion

/// region Components

export const MyImage = observer<MyProps, HTMLDivElement>(function MyImage(props, forwardRef) {
  const obsProps = useAsObservableSource(_.omit(props, [])),
    store = useVipLocalStore((ws) => new MyImageStore(ws, obsProps)),
    {
      noImageContent, filename, largeImage, objectFit, blurOnLoad, customLinkOff, style, loader, lazy, showCode, className, draggable, linkUrl, ref, convertToDataURL, onGetBackgroundColor, onContextMenu, 'data-index': index, 'data-active': active, 'data-path': path, 'data-path-index': pathIndex, onCreateStore, onMeasureImage,
      imageId, title, transparent, rawImageSize, ...rest
    } = props,
    {loaded, hasError, imageStyle, setImageRef, imageSrc, noImage, qrImage, onImageLoad, onImageLoadError, defaultImageUrl, retryCount} = store

  useEffect(() => store.registerEffects(), [])

  const [measureRef, {width, height}] = useMeasure()

  useEffect(() => {
    if (onMeasureImage && store.image && loaded) {
      // console.log(`Measuring image - expensive!!`)
      const {image, image: {width, height}} = store // Very expensive will cause reflow

      onMeasureImage({width, height, image})
    }
  }, [width, height, loaded])

  useEffect(() => {
    if (!store.addSecurityToken && !store.retryFromC8 && store.image && store.image?.src != imageSrc) {
      runInAction(() => {
        store.image = null
        store.qrImage = null
        store.imageLoadError = null
      })
    }
  }, [imageSrc])

  const passthruProps = _.omit(rest, ['imageUrl', 'c8ImageUrl', 'src'])

  return (
    <MyImageStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           data-blur-on-load={blurOnLoad && store.animateOnLoad}
           data-loaded={loaded}
           data-no-image={noImage}
           data-error={hasError}
           data-type='VipImage'
           data-url={hasError ? imageSrc : undefined}
           // data-import-url={DEBUG ? store.serverlessImportUrl : undefined}
           data-object-fit={objectFit}
           data-show-qr={showCode && qrImage ? true : false}
           data-retry={retryCount}
           title={title}
           style={{
             '--image-source-object-fit': objectFit,
             ...(style ?? {})
           } as any}
           data-active={active}
           data-index={index}
           data-path={path}
           data-path-index={pathIndex}
           ref={forwardRef}
        //data-image-url={imageSrc}
           data-selected={props['data-selected']}
           onContextMenu={onContextMenu}>
        <div
          ref={onMeasureImage ? measureRef : undefined}
          className={css.contents} data-type='contents'>
          {noImage
           ? <span data-no-image={true} {...passthruProps}>
         {defaultImageUrl?.startsWith('#') || defaultImageUrl?.startsWith('rgba(')
          ? <div className={classNames(css.image, css.colorCodeBackground)}
                 style={{background: defaultImageUrl}} {...passthruProps}/>
          : (noImageContent ?? <LinkOff className={classNames({[css.linkOffCustom]: customLinkOff})}/>)}
       </span>
           : <LazyImage
             {...passthruProps}
             lazy={lazy ?? false}
             key={imageSrc}
             className={classNames(css.image, {[css.largeImage]: largeImage})}
             ref={setImageRef}
             // crossOrigin='anonymous'
             //srcSet={srcSet}
             src={imageSrc}
             onLoad={onImageLoad}
             draggable={draggable}
             // alt={imageSrc}
             onError={onImageLoadError}
             style={imageStyle}
           />}
        </div>
        {/*<LazyImage className={css.qr} src={qrImage} onLoad={onQrImageLoad}/>*/}
      </div>
    </MyImageStoreContext.Provider>)
}, {forwardRef: true})




// WEBPACK FOOTER //
// ./src/shared/components/ui/components/QRCode/MyImage.tsx